import { useEffect, useState } from 'react';
import type { Notification } from '@/lib/db/queries/notifications';
import { getRealtimeClient } from '@/lib/realtime/client';

/**
 * Real-time hook for notifications
 * Uses WebSocket connection to Arcadia API `/realtime`
 */
export function useRealtimeNotifications(userId: string) {
  const [notifications, setNotifications] = useState<Notification[]>([]);

  useEffect(() => {
    if (!userId) return;

    const client = getRealtimeClient();
    const channel = `notifications_user_${userId}`;

    // Connect to realtime service
    client.connect().catch((error) => {
      console.error('[useRealtimeNotifications] Failed to connect:', error);
    });

    // Subscribe to notifications for this user
    const unsubscribe = client.subscribe(channel, (event) => {
      if (event.event === 'INSERT' && event.new) {
        // Transform database row to Notification type
        const notification: Notification = {
          id: event.new.id,
          userId: event.new.user_id,
          type: event.new.type,
          title: event.new.title,
          body: event.new.body,
          metadata: event.new.metadata || {},
          read: event.new.read || false,
          createdAt: event.new.created_at,
        };

        setNotifications((prev) => {
          // Avoid duplicates
          if (prev.some(n => n.id === notification.id)) {
            return prev;
          }
          return [notification, ...prev].slice(0, 100); // Keep last 100
        });
      } else if (event.event === 'UPDATE' && event.new) {
        // Update existing notification
        setNotifications((prev) =>
          prev.map((n) =>
            n.id === event.new.id
              ? {
                  ...n,
                  read: event.new.read || n.read,
                  title: event.new.title || n.title,
                  body: event.new.body || n.body,
                  metadata: event.new.metadata || n.metadata,
                }
              : n
          )
        );
      }
    });

    return () => {
      unsubscribe();
    };
  }, [userId]);

  return notifications;
}
